// =============================================================================
//  Copyright : 2026 semantics GmbH
//  Date      : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163319 $
//  Purpose   : utility functions for date handling
// =============================================================================
// ---Framework imports
import { dict, isNil, isObject, isString } from 'ldash';
import log from 'uix/logging';
// ---Globals
const DAY_MS = 24 * 60 * 60 * 1000;
const pad = (value, size = 2)=>String(value).padStart(size, '0');
const toDate = (value)=>value instanceof Date ? new Date(value.getTime()) : new Date(value);
const shortMonth = (date, locale)=>new Intl.DateTimeFormat(locale, {
        month: 'short'
    }).format(date);
const mediumDateLikeDateFns = (date, locale)=>new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    }).format(date).replace(',', '');
const isoWeek = (dateValue)=>{
    let date = toDate(dateValue);
    let d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    let day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    let yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / DAY_MS + 1) / 7);
};
const applyFormatTokens = (format, values)=>{
    let out = '';
    let i = 0;
    let tokens = Object.keys(values).sort((a, b)=>b.length - a.length);
    while(i < format.length){
        let char = format[i];
        if (char === "'") {
            // date-fns-style escaped literals
            i += 1;
            while(i < format.length){
                if (format[i] === "'") {
                    if (format[i + 1] === "'") {
                        out += "'";
                        i += 2;
                        continue;
                    }
                    i += 1;
                    break;
                }
                out += format[i];
                i += 1;
            }
            continue;
        }
        let matched = false;
        for (let token of tokens){
            if (format.slice(i, i + token.length) === token) {
                out += values[token];
                i += token.length;
                matched = true;
                break;
            }
        }
        if (!matched) {
            out += char;
            i += 1;
        }
    }
    return out;
};
const asValidDate = (value)=>{
    let date = toDate(value);
    return isValid(date) ? date : new Date(NaN);
};
const negateDuration = (duration)=>{
    duration = duration || {};
    return {
        years: -(duration.years || 0),
        months: -(duration.months || 0),
        weeks: -(duration.weeks || 0),
        days: -(duration.days || 0),
        hours: -(duration.hours || 0),
        minutes: -(duration.minutes || 0),
        seconds: -(duration.seconds || 0),
        milliseconds: -(duration.milliseconds || 0)
    };
};
export const isValid = (value)=>!Number.isNaN(toDate(value).getTime());
export const parseISO = (value)=>new Date(value);
export const add = (dateValue, duration)=>{
    duration = duration || {};
    let date = toDate(dateValue);
    date.setFullYear(date.getFullYear() + (duration.years || 0));
    date.setMonth(date.getMonth() + (duration.months || 0));
    let deltaMs = ((duration.weeks || 0) * 7 + (duration.days || 0)) * DAY_MS + (duration.hours || 0) * 60 * 60 * 1000 + (duration.minutes || 0) * 60 * 1000 + (duration.seconds || 0) * 1000 + (duration.milliseconds || 0);
    return new Date(date.getTime() + deltaMs);
};
export const sub = (dateValue, duration)=>add(dateValue, negateDuration(duration));
export const addDays = (dateValue, days)=>add(dateValue, {
        days
    });
export const addMonths = (dateValue, months)=>add(dateValue, {
        months
    });
export const addYears = (dateValue, years)=>add(dateValue, {
        years
    });
export const subDays = (dateValue, days)=>sub(dateValue, {
        days
    });
export const subWeeks = (dateValue, weeks)=>sub(dateValue, {
        weeks
    });
export const subMonths = (dateValue, months)=>sub(dateValue, {
        months
    });
export const subYears = (dateValue, years)=>sub(dateValue, {
        years
    });
export const subMilliseconds = (dateValue, milliseconds)=>sub(dateValue, {
        milliseconds
    });
export const differenceInMilliseconds = (dateLeft, dateRight)=>Math.trunc(asValidDate(dateLeft) - asValidDate(dateRight));
export const differenceInSeconds = (dateLeft, dateRight)=>Math.trunc((asValidDate(dateLeft) - asValidDate(dateRight)) / 1000);
export const differenceInMinutes = (dateLeft, dateRight)=>Math.trunc((asValidDate(dateLeft) - asValidDate(dateRight)) / (60 * 1000));
export const differenceInDays = (dateLeft, dateRight)=>Math.trunc((asValidDate(dateLeft) - asValidDate(dateRight)) / DAY_MS);
export const differenceInMonths = (dateLeft, dateRight)=>{
    let left = asValidDate(dateLeft);
    let right = asValidDate(dateRight);
    if (!isValid(left) || !isValid(right)) return NaN;
    let sign = left >= right ? 1 : -1;
    let later = sign > 0 ? left : right;
    let earlier = sign > 0 ? right : left;
    let months = (later.getFullYear() - earlier.getFullYear()) * 12 + (later.getMonth() - earlier.getMonth());
    if (later.getDate() < earlier.getDate()) {
        months -= 1;
    }
    return months * sign;
};
export const differenceInYears = (dateLeft, dateRight)=>{
    let left = asValidDate(dateLeft);
    let right = asValidDate(dateRight);
    if (!isValid(left) || !isValid(right)) return NaN;
    let years = left.getFullYear() - right.getFullYear();
    let leftMonth = left.getMonth();
    let rightMonth = right.getMonth();
    if (leftMonth < rightMonth || leftMonth === rightMonth && left.getDate() < right.getDate()) {
        years -= 1;
    }
    return years;
};
export const startOfDay = (dateValue)=>{
    let date = toDate(dateValue);
    date.setHours(0, 0, 0, 0);
    return date;
};
export const startOfWeek = (dateValue, { weekStartsOn = 0 } = {})=>{
    let date = startOfDay(dateValue);
    let delta = (date.getDay() - weekStartsOn + 7) % 7;
    return subDays(date, delta);
};
export const lastDayOfWeek = (dateValue, { weekStartsOn = 0 } = {})=>addDays(startOfWeek(dateValue, {
        weekStartsOn
    }), 6);
export const startOfMonth = (dateValue)=>{
    let date = startOfDay(dateValue);
    date.setDate(1);
    return date;
};
export const startOfYear = (dateValue)=>{
    let date = startOfDay(dateValue);
    date.setMonth(0, 1);
    return date;
};
export const lastDayOfMonth = (dateValue)=>{
    let date = startOfMonth(dateValue);
    date.setMonth(date.getMonth() + 1, 0);
    return date;
};
export const lastDayOfYear = (dateValue)=>{
    let date = startOfYear(dateValue);
    date.setFullYear(date.getFullYear() + 1, 0, 0);
    return date;
};
export const getDate = (dateValue)=>toDate(dateValue).getDate();
export const getMonth = (dateValue)=>toDate(dateValue).getMonth();
export const getYear = (dateValue)=>toDate(dateValue).getFullYear();
export const getDecade = (dateValue)=>Math.floor(getYear(dateValue) / 10) * 10;
export const getDaysInMonth = (dateValue)=>{
    let date = toDate(dateValue);
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};
export const isAfter = (dateValue, dateToCompare)=>asValidDate(dateValue) > asValidDate(dateToCompare);
export const isToday = (dateValue)=>isoDate(dateValue) === isoToday();
export const isYesterday = (dateValue)=>isoDate(dateValue) === isoDate(subDays(new Date(), 1));
export const parse = (dateString, formatString, baseDate = new Date())=>{
    if (formatString === 'yyyy-MM-dd') {
        let match = String(dateString || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) {
            return new Date(NaN);
        }
        let year = parseInt(match[1], 10);
        let month = parseInt(match[2], 10) - 1;
        let day = parseInt(match[3], 10);
        let parsed = new Date(year, month, day);
        if (parsed.getFullYear() !== year || parsed.getMonth() !== month || parsed.getDate() !== day) {
            return new Date(NaN);
        }
        return parsed;
    }
    return toDate(baseDate);
};
export const formatISO = (dateValue, { representation } = {})=>{
    let date = asValidDate(dateValue);
    if (!isValid(date)) return '';
    let datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    if (representation === 'date') {
        return datePart;
    }
    let timePart = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    return `${datePart}T${timePart}`;
};
const LOCALES = {
    de: 'de-DE',
    en: 'en-US'
};
// date-fn format
const DT_ISO = 'yyyy-LL-dd HH:mm:ss';
const DT_ISO_DATE = 'yyyy-LL-dd';
let LANG_LOCALE = 'de-DE';
export const switchLanguageFormat = (lang)=>{
    LANG_LOCALE = LOCALES[lang] ?? 'de-DE';
};
export const formatDate = (date, format = 'PP')=>{
    if (isNil(date)) {
        return '';
    }
    if (isString(date)) {
        date = parseISO(date);
    }
    if (format == 'ISO') {
        format = DT_ISO;
    } else if (format == 'ISO_DATE') {
        format = DT_ISO_DATE;
    }
    try {
        return nativeDateFormat(date, format, LANG_LOCALE);
    } catch (e) {
        // RangeError
        log.warn('invalid date or format: "%o" with %o', date, format);
        return null;
    }
};
export const formatDateTime = (date, format = 'PPp')=>formatDate(date, format);
export const formatTime = (date, format = 'pp')=>formatDate(date, format);
export const formatDistance = (date1, date2, options)=>{
    /* format distance between two date values. If date2 is omitted, date2 is now.
   */ if (!options) {
        if (isObject(date2)) {
            options = date2;
            date2 = null;
        } else {
            options = {};
        }
    }
    date1 = isString(date1) ? new Date(date1) : date1;
    date2 = isString(date2) ? new Date(date2) : date2;
    if (options.onlyUnits) {
        return formatSeconds(timeDeltaSeconds(date1, date2 ? date2 : new Date()));
    }
    if (date2) {
        return nativeFormatDistance(date1, date2, {
            ...options,
            locale: LANG_LOCALE
        });
    } else {
        return nativeFormatDistance(date1, new Date(), {
            ...options,
            locale: LANG_LOCALE
        });
    }
};
const nativeDateFormat = (value, format, locale)=>{
    let date = new Date(value);
    if (!isValid(date)) throw new RangeError(`invalid date: ${value}`);
    if (format === 'P') {
        return new Intl.DateTimeFormat(locale, {
            dateStyle: 'short'
        }).format(date);
    } else if (format === 'PP') {
        return mediumDateLikeDateFns(date, locale);
    } else if (format === 'PPPP') {
        return new Intl.DateTimeFormat(locale, {
            dateStyle: 'full'
        }).format(date);
    } else if (format === 'pp') {
        return new Intl.DateTimeFormat(locale, {
            timeStyle: 'short'
        }).format(date);
    } else if (format === 'PPp') {
        return `${mediumDateLikeDateFns(date, locale)} ${applyFormatTokens('HH:mm', {
            HH: String(date.getHours()).padStart(2, '0'),
            mm: String(date.getMinutes()).padStart(2, '0')
        })}`;
    }
    let values = {
        yyyy: String(date.getFullYear()).padStart(4, '0'),
        yy: String(date.getFullYear()).slice(-2),
        qqq: `Q${Math.floor(date.getMonth() / 3) + 1}`,
        LLL: shortMonth(date, locale),
        wo: String(isoWeek(date)) + (String(locale).startsWith('de') ? '.' : ''),
        ww: String(isoWeek(date)).padStart(2, '0'),
        k: String(date.getHours() || 24),
        LL: String(date.getMonth() + 1).padStart(2, '0'),
        MM: String(date.getMonth() + 1).padStart(2, '0'),
        dd: String(date.getDate()).padStart(2, '0'),
        HH: String(date.getHours()).padStart(2, '0'),
        mm: String(date.getMinutes()).padStart(2, '0'),
        ss: String(date.getSeconds()).padStart(2, '0'),
        SSS: String(date.getMilliseconds()).padStart(3, '0')
    };
    return applyFormatTokens(format, values);
};
const nativeFormatDistance = (date1, date2, options = {})=>{
    let d1 = new Date(date1);
    let d2 = new Date(date2);
    if (!isValid(d1) || !isValid(d2)) return '';
    let deltaSeconds = Math.round((d1 - d2) / 1000);
    let absSeconds = Math.abs(deltaSeconds);
    let units = [
        [
            'year',
            365 * 24 * 60 * 60
        ],
        [
            'month',
            30 * 24 * 60 * 60
        ],
        [
            'day',
            24 * 60 * 60
        ],
        [
            'hour',
            60 * 60
        ],
        [
            'minute',
            60
        ],
        [
            'second',
            1
        ]
    ];
    let [unit, unitSeconds] = units.find(([, secs])=>absSeconds >= secs) || [
        'second',
        1
    ];
    let value = Math.round(absSeconds / unitSeconds);
    if (options.addSuffix) {
        let rtf = new Intl.RelativeTimeFormat(options.locale || LANG_LOCALE, {
            numeric: 'always'
        });
        return rtf.format(Math.sign(deltaSeconds) * value, unit);
    }
    let pr = new Intl.PluralRules(options.locale || LANG_LOCALE);
    let plural = pr.select(value);
    let enNames = {
        year: {
            one: 'year',
            other: 'years'
        },
        month: {
            one: 'month',
            other: 'months'
        },
        day: {
            one: 'day',
            other: 'days'
        },
        hour: {
            one: 'hour',
            other: 'hours'
        },
        minute: {
            one: 'minute',
            other: 'minutes'
        },
        second: {
            one: 'second',
            other: 'seconds'
        }
    };
    let deNames = {
        year: {
            one: 'Jahr',
            other: 'Jahre'
        },
        month: {
            one: 'Monat',
            other: 'Monate'
        },
        day: {
            one: 'Tag',
            other: 'Tage'
        },
        hour: {
            one: 'Stunde',
            other: 'Stunden'
        },
        minute: {
            one: 'Minute',
            other: 'Minuten'
        },
        second: {
            one: 'Sekunde',
            other: 'Sekunden'
        }
    };
    let localizedNames = String(options.locale || LANG_LOCALE).startsWith('de') ? deNames : enNames;
    return `${value} ${localizedNames[unit][plural] || localizedNames[unit].other}`;
};
const timeDeltaSeconds = (t1, t2)=>{
    let secs = (new Date(t2) - new Date(t1)) / 1000;
    return secs;
};
export const formatSeconds = (secs)=>{
    if (isNil(secs) || secs === '') {
        return '';
    } else if (secs < 60) {
        return `${secs.toFixed(1)}s`;
    } else if (secs < 3600) {
        let mins = Math.floor(secs / 60);
        if (secs % 60 > 0) {
            return `${mins}min ${(secs % 60).toFixed(0)}s`;
        } else {
            return `${mins}min`;
        }
    } else {
        let hours = Math.floor(secs / 3600);
        secs = secs % 3600;
        let mins = Math.floor(secs / 60);
        return `${hours}h ${mins}min`;
    }
};
export const formatMinutes = (minutes)=>formatSeconds(minutes * 60);
// ---Exports
export const isoToday = ()=>formatISO(new Date(), {
        representation: 'date'
    });
export const isoDate = (d)=>formatISO(d, {
        representation: 'date'
    });
const granularities = [
    'year',
    'quart',
    'month',
    'week',
    'day'
];
export const interval2granularity = dict(granularities.map((g)=>[
        g,
        g[0].toUpperCase()
    ]));
export const granularity2interval = dict(granularities.map((g)=>[
        g[0].toUpperCase(),
        g
    ]));
// limits for granularities
// attention: should be ascending sorted
export const GranularityStrategy = {
    D: null,
    W: null,
    M: null,
    Q: null,
    Y: null
};
